/**
 * The record and the onboarding state, loaded once and shared by every screen.
 *
 *   holders     the roster (public/data/roster/holders.json, read-only)
 *   summary     its headline numbers
 *   rows        onboarding rows keyed by holder id (the portal's own records)
 *   unmatched   bot farmers whose pin fell outside every farm
 *   staff       who may be assigned
 *
 * Derived here, so every screen counts the same way: a holder's stage is the
 * row's stage or 'target' when nobody has touched them.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiPost, apiUrl, type Loadable } from '../api/client';
import type { Holder, OnboardingEvent, OnboardingRow, RosterSummary, Stage, Staff, SyncRun, UnmatchedPin } from '@shared/model';
import { STAGES, stageIndex } from '@shared/model';

export interface VillageStat {
  village: string;
  holders: number;
  farms: number;
  acres: number;
  byStage: Record<Stage, number>;
  /** Holders at or beyond each stage. */
  reached: Record<Stage, number>;
  pinInsideOwn: number;
  pinElsewhere: number;
}

interface OnboardingPayload {
  rows: OnboardingRow[];
  unmatched: UnmatchedPin[];
  lastSync: SyncRun | null;
  whatsapp: { number: string; direct: boolean };
}

export interface FarmRef {
  pid: string;
  khasra: string;
  village: string;
  acres: number;
  pin: [number, number];
  in_block: boolean;
  holders: string[];
}

interface Ctx {
  holders: Loadable<Holder[]>;
  summary: RosterSummary | null;
  rows: Map<string, OnboardingRow>;
  farmIndex: Map<string, FarmRef>;
  unmatched: UnmatchedPin[];
  lastSync: SyncRun | null;
  whatsapp: { number: string; direct: boolean } | null;
  staff: Staff[];
  byId: Map<string, Holder>;
  stageOf: (holderId: string) => Stage;
  villages: VillageStat[];
  reached: Record<Stage, number>;
  patch: (holderId: string, body: Record<string, unknown>) => Promise<{ row: OnboardingRow; events: OnboardingEvent[] } | string>;
  sync: () => Promise<string>;
  reload: () => void;
  reloadStaff: () => void;
}

const RosterCtx = createContext<Ctx | null>(null);

const emptyStages = (): Record<Stage, number> => Object.fromEntries(STAGES.map((s) => [s, 0])) as Record<Stage, number>;

export function RosterProvider({ children }: { children: ReactNode }): JSX.Element {
  const [holders, setHolders] = useState<Loadable<Holder[]>>({ status: 'loading' });
  const [summary, setSummary] = useState<RosterSummary | null>(null);
  const [onb, setOnb] = useState<OnboardingPayload | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    Promise.all([
      fetch(apiUrl('/data/roster/holders.json')).then((r) => {
        if (!r.ok) throw new Error(`The roster did not load (${r.status}). Run tools/build_roster.py.`);
        return r.json() as Promise<Holder[]>;
      }),
      fetch(apiUrl('/data/roster/summary.json')).then((r) => (r.ok ? (r.json() as Promise<RosterSummary>) : null)).catch(() => null),
    ])
      .then(([h, s]) => {
        if (!live) return;
        setHolders({ status: 'ready', data: h });
        setSummary(s);
      })
      .catch((e: Error) => live && setHolders({ status: 'error', message: e.message }));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    void apiGet<OnboardingPayload>('/api/onboarding').then((r) => {
      if (live && r.status === 'ready' && r.data) setOnb(r.data);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  // Several people work on the portal at once, from different phones and
  // offices: what one records must show on the others' screens without a
  // reload. The onboarding records are re-read every half minute while the
  // page is visible, and the moment it comes back into view.
  useEffect(() => {
    const bump = (): void => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    };
    const timer = window.setInterval(bump, 30_000);
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', bump);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', bump);
    };
  }, []);

  const reloadStaff = useCallback(() => {
    void apiGet<Staff[]>('/api/staff').then((r) => r.status === 'ready' && r.data && setStaff(r.data));
  }, []);
  useEffect(reloadStaff, [reloadStaff]);

  const rows = useMemo(() => new Map((onb?.rows ?? []).map((r) => [r.holder_id, r])), [onb]);
  const byId = useMemo(() => new Map((holders.status === 'ready' ? holders.data : []).map((h) => [h.id, h])), [holders]);
  /** Every named farm by khasra id, with the holders on it, from the roster itself. */
  const farmIndex = useMemo(() => {
    const m = new Map<string, FarmRef>();
    for (const h of holders.status === 'ready' ? holders.data : []) {
      for (const f of h.farms) {
        const cur = m.get(f.pid);
        if (cur) cur.holders.push(h.id);
        else m.set(f.pid, { pid: f.pid, khasra: f.khasra, village: h.village, acres: f.acres, pin: f.pin, in_block: f.in_block, holders: [h.id] });
      }
    }
    return m;
  }, [holders]);
  const stageOf = useCallback((id: string): Stage => rows.get(id)?.stage ?? 'target', [rows]);

  const { villages, reached } = useMemo(() => {
    const list = holders.status === 'ready' ? holders.data : [];
    const perV = new Map<string, VillageStat>();
    const total = emptyStages();
    for (const h of list) {
      let v = perV.get(h.village);
      if (!v) {
        v = { village: h.village, holders: 0, farms: 0, acres: 0, byStage: emptyStages(), reached: emptyStages(), pinInsideOwn: 0, pinElsewhere: 0 };
        perV.set(h.village, v);
      }
      const row = rows.get(h.id);
      const stage = row?.stage ?? 'target';
      v.holders++;
      v.farms += h.farm_count;
      v.acres += h.acres;
      v.byStage[stage]++;
      const idx = stageIndex(stage);
      for (const s of STAGES) if (stageIndex(s) <= idx) { v.reached[s]++; total[s]++; }
      if (row?.pin_lat != null) {
        if (row.pin_inside_own) v.pinInsideOwn++; else v.pinElsewhere++;
      }
    }
    const vs = [...perV.values()].sort((a, b) => a.village.localeCompare(b.village));
    for (const v of vs) {
      const s = summary?.per_village[v.village];
      if (s) { v.farms = s.farms; v.acres = s.acres; }
    }
    return { villages: vs, reached: total };
  }, [holders, rows, summary]);

  const patch = useCallback(async (holderId: string, body: Record<string, unknown>) => {
    const r = await apiPost<{ row: OnboardingRow; events: OnboardingEvent[] }>(`/api/onboarding/${encodeURIComponent(holderId)}`, body);
    if (r.status !== 'ready') return r.status === 'error' || r.status === 'not_configured' ? r.message : 'Not saved.';
    setOnb((cur) => (cur ? { ...cur, rows: [...cur.rows.filter((x) => x.holder_id !== holderId), r.data.row] } : cur));
    return r.data;
  }, []);

  const sync = useCallback(async (): Promise<string> => {
    const r = await apiPost<{ farmers_read: number; matched: number; outside: number; source: string }>('/api/sync/bot', {});
    setNonce((n) => n + 1);
    if (r.status !== 'ready') return r.status === 'error' || r.status === 'not_configured' ? r.message : 'Sync failed.';
    return `${r.data.farmers_read} bot farmers read (${r.data.source}): ${r.data.matched} placed on the record, ${r.data.outside} outside it.`;
  }, []);

  // Stable identity: screens run `reload` from an effect on mount, and a fresh
  // function on every data change would make that effect fire after each
  // answer - a request loop (seen as ~6 reads a second on the dashboard).
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<Ctx>(
    () => ({
      holders, summary, rows, farmIndex, unmatched: onb?.unmatched ?? [], lastSync: onb?.lastSync ?? null, whatsapp: onb?.whatsapp ?? null, staff,
      byId, stageOf, villages, reached, patch, sync, reload, reloadStaff,
    }),
    [holders, summary, rows, farmIndex, onb, staff, byId, stageOf, villages, reached, patch, sync, reload, reloadStaff],
  );
  return <RosterCtx.Provider value={value}>{children}</RosterCtx.Provider>;
}

export function useRoster(): Ctx {
  const ctx = useContext(RosterCtx);
  if (!ctx) throw new Error('useRoster outside RosterProvider');
  return ctx;
}

/** The farm map, loaded on first use and kept for the session. */
let farmsCache: Promise<GeoJSON.FeatureCollection> | null = null;
export function loadFarms(): Promise<GeoJSON.FeatureCollection> {
  if (!farmsCache) {
    farmsCache = fetch(apiUrl('/data/roster/farms.web.geojson')).then((r) => {
      if (!r.ok) throw new Error(`The farm map did not load (${r.status})`);
      return r.json() as Promise<GeoJSON.FeatureCollection>;
    });
    farmsCache.catch(() => {
      farmsCache = null;
    });
  }
  return farmsCache;
}
